/** Regains v2 validation lab (port of qa/regains_lab.html, same address
 *  /qa/regains/<code>).
 *
 * Every detected regain on a pitch at its true position, with video
 * deep-links (now to /match-video — the Jinja page still linked the
 * legacy /game-match-summary address) and correct/incorrect verdicts
 * captured as ground truth via the same /api/v1/qa/regains endpoints.
 */
import { useEffect, useState } from 'react'
import { useParams } from 'react-router-dom'

import { api } from '../../lib/api'
import { NO_FRAME_RATE_NOTE, servedFps } from '../../lib/frameRate'
import { pathSeg } from '../../lib/urls'
import './qa.css'

type Ev = {
  frame: number; team: number; kind: string; subtype: string
  x_att: number; y: number; zone: string; phase: number
  counterpress: boolean; confidence: number
  loser: number; opponent_held_seconds: number; possession_source: string
}
type Label = { frame: number; team: number; verdict: string; labeled_by: string }
type Payload = {
  game_code: string; org_code: string; fps: number | null; has_video: boolean
  events: Ev[]; labels: Label[]
}

const COLORS: Record<string, string> = {
  interception: '#60a5fa', tackle: '#f59e0b', loose_ball: '#e5e7eb',
  unknown: '#a78bfa', restart: '#9ca3af',
}
const VERDICTS = [
  ['correct', 'correct'],
  ['not_a_regain', 'not a regain'],
  ['actually_restart', 'actually a restart'],
  ['wrong_team', 'wrong team'],
  ['wrong_spot', 'wrong location'],
] as const

const px = (x: number) => 25 + (x + 52.5) / 105 * 1000
const py = (y: number) => 15 + (y + 34) / 68 * 650

export default function RegainsLab() {
  const { gameCode = '' } = useParams<{ gameCode: string }>()
  const [data, setData] = useState<Payload | null>(null)
  const [error, setError] = useState<string | null>(null)
  const [team, setTeam] = useState(0)
  const [kind, setKind] = useState('regain')
  const [subtype, setSubtype] = useState('all')
  const [cpOnly, setCpOnly] = useState(false)
  const [selected, setSelected] = useState<Ev | null>(null)
  const [saveError, setSaveError] = useState<string | null>(null)

  useEffect(() => {
    api<Payload>(`/api/v1/qa/regains/${pathSeg(gameCode)}`)
      .then((d) => {
        setData(d)
        const teams = [...new Set(d.events.map((e) => e.team))].sort()
        if (teams.length) setTeam(teams[0]!)
      })
      .catch((e: Error) => setError(e.message))
  }, [gameCode])

  if (error) return <div className="qa-lab-wrap"><div className="err">{error}</div></div>
  if (!data) return <div className="qa-lab-wrap"><div className="sub">{gameCode} - loading...</div></div>
  // the game's recorded rate; with none an event's minute is not given
  const fps = servedFps(data.fps)

  const labelFor = (e: Ev) => data.labels.find(
    (l) => l.frame === e.frame && l.team === e.team)

  const events = data.events.filter((e) =>
    e.team === team &&
    (kind === 'all' || e.kind === kind) &&
    (subtype === 'all' || e.subtype === subtype) &&
    (!cpOnly || e.counterpress))

  const teams = [...new Set(data.events.map((e) => e.team))].sort()
  const subtypes = [...new Set(data.events.map((e) => e.subtype))].sort()
  const regains = data.events.filter((e) => e.kind === 'regain')
  const labeled = data.labels.length
  const correct = data.labels.filter((l) => l.verdict === 'correct').length

  const setVerdict = async (verdict: string) => {
    if (!selected) return
    setSaveError(null)
    try {
      await api(`/api/v1/qa/regains/${pathSeg(gameCode)}/label`, {
        method: 'POST',
        body: JSON.stringify({ frame: selected.frame, team: selected.team, verdict }),
      })
      const existing = labelFor(selected)
      if (existing) { existing.verdict = verdict; existing.labeled_by = 'you' }
      else data.labels.push({ frame: selected.frame, team: selected.team,
                              verdict, labeled_by: 'you' })
      setData({ ...data })
    } catch (e) {
      // FormationsLab shows 'save failed'; a silent catch here meant a
      // 500 looked identical to success (PR #385 review).
      setSaveError(e instanceof Error ? e.message : 'save failed')
    }
  }

  const selLabel = selected ? labelFor(selected) : null

  return (
    <div className="qa-lab-wrap">
      <div className="wrap">
        <h2>Regains Lab</h2>
        <div className="sub">
          {data.game_code} ({data.org_code}) - {regains.length} regains,{' '}
          {data.events.length - regains.length} restarts, {data.labels.length} labeled
        </div>

        <div className="controls">
          <label>team{' '}
            <select value={team} onChange={(e) => setTeam(Number(e.target.value))}>
              {teams.map((t) => <option key={t} value={t}>team {t}</option>)}
            </select>
          </label>
          <label>kind{' '}
            <select value={kind} onChange={(e) => setKind(e.target.value)}>
              <option value="regain">regains</option>
              <option value="restart">restarts</option>
              <option value="all">both</option>
            </select>
          </label>
          <label>subtype{' '}
            <select value={subtype} onChange={(e) => setSubtype(e.target.value)}>
              <option value="all">all</option>
              {subtypes.map((s) => <option key={s} value={s}>{s}</option>)}
            </select>
          </label>
          <label>
            <input type="checkbox" checked={cpOnly}
                   onChange={(e) => setCpOnly(e.target.checked)} /> counterpress only
          </label>
          <span className="sub">{events.length} events</span>
        </div>

        <div className="layout">
          <div className="panel">
            <svg id="pitch" viewBox="0 0 1050 680">
              <rect x="0" y="0" width="1050" height="680" fill="#0b6e2f" />
              <rect x="25" y="15" width="1000" height="650" fill="none" stroke="#ffffff88" strokeWidth="2" />
              <line x1="525" y1="15" x2="525" y2="665" stroke="#ffffff88" strokeWidth="2" />
              <circle cx="525" cy="340" r="80" fill="none" stroke="#ffffff88" strokeWidth="2" />
              <rect x="25" y="205" width="120" height="270" fill="none" stroke="#ffffff66" strokeWidth="2" />
              <rect x="905" y="205" width="120" height="270" fill="none" stroke="#ffffff66" strokeWidth="2" />
              <polygon points="1000,330 1020,340 1000,350" fill="#ffffffcc" />
              {events.map((e) => {
                const lbl = labelFor(e)
                const ring = lbl ? (lbl.verdict === 'correct' ? '#047857' : '#b91c1c') : 'none'
                return (
                  <circle key={`${e.frame}-${e.team}`}
                          cx={px(e.x_att)} cy={py(e.y)} r={e.counterpress ? 11 : 8}
                          fill={COLORS[e.subtype] || '#e5e7eb'}
                          opacity={0.45 + (e.confidence || 0.5) * 0.55}
                          stroke={ring} strokeWidth="3" style={{ cursor: 'pointer' }}
                          onClick={() => setSelected(e)} />
                )
              })}
            </svg>
            <div className="legend">
              each dot = one event at its true pitch position, attacking → for the selected
              team. color: <span style={{ color: '#60a5fa' }}>interception</span> /{' '}
              <span style={{ color: '#f59e0b' }}>tackle</span> /{' '}
              <span style={{ color: '#e5e7eb' }}>loose ball</span> /{' '}
              <span style={{ color: '#a78bfa' }}>unknown</span> /{' '}
              <span style={{ color: '#9ca3af' }}>restart</span>.
              ring: <span style={{ color: '#047857' }}>labeled correct</span> /{' '}
              <span style={{ color: '#b91c1c' }}>labeled wrong</span>. Click a dot to review.
            </div>
          </div>

          <div className="panel side">
            <h5>Event</h5>
            {!selected && <div className="sub">click a dot</div>}
            {selected && (
              <>
                <div className="detail-row"><span>kind</span><b>{selected.kind}</b></div>
                <div className="detail-row"><span>subtype</span><b>{selected.subtype}</b></div>
                <div className="detail-row"><span>team</span><b>{selected.team} (from {selected.loser})</b></div>
                <div className="detail-row"><span>minute</span>
                  <b>{fps === null ? NO_FRAME_RATE_NOTE
                    : `${(selected.frame / fps / 60).toFixed(1)} (phase ${selected.phase})`}</b></div>
                <div className="detail-row"><span>zone</span><b>{selected.zone}</b></div>
                <div className="detail-row"><span>counterpress</span><b>{selected.counterpress ? 'yes' : 'no'}</b></div>
                <div className="detail-row"><span>opp held</span><b>{selected.opponent_held_seconds}s</b></div>
                <div className="detail-row"><span>confidence</span><b>{selected.confidence}</b></div>
                <div className="detail-row"><span>possession src</span><b>{selected.possession_source}</b></div>
                {data.has_video && (
                  <a className="watch" target="_blank" rel="noopener noreferrer"
                     href={`/match-room/${pathSeg(gameCode)}?frame=${selected.frame}`}>
                    ▶ watch this moment
                  </a>
                )}
                {selLabel && (
                  <div className="labelnote">
                    labeled {selLabel.verdict} by {selLabel.labeled_by.split('@')[0]}
                  </div>
                )}
                {saveError && <div className="err">save failed: {saveError}</div>}
                <div className="verdicts">
                  {VERDICTS.map(([v, label]) => (
                    <button key={v}
                            className={selLabel && selLabel.verdict === v ? 'active' : ''}
                            onClick={() => setVerdict(v)}>
                      {label}
                    </button>
                  ))}
                </div>
              </>
            )}
            <div className="stat">
              {labeled
                ? `${labeled} labeled, ${correct} correct ` +
                  `(${Math.round(correct / labeled * 100)}% precision so far)` : ''}
            </div>
          </div>
        </div>
      </div>
    </div>
  )
}
